import { Phone, Mail, Globe, MapPin, Facebook, Instagram, Youtube } from "lucide-react";
import { LOGO_URL, CONTACT, SOCIALS } from "../../lib/content";

const TikTokIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 1 1-2.59-2.59c.27 0 .53.04.77.12v-3.2a5.8 5.8 0 0 0-.77-.05A5.79 5.79 0 1 0 15.65 15.4V9.01a7.35 7.35 0 0 0 4.35 1.4V7.3a4.28 4.28 0 0 1-3.4-1.48Z" />
  </svg>
);

const SOCIAL_ICONS = { Facebook, Instagram, Youtube, TikTok: TikTokIcon };

export const Footer = () => {
  return (
    <footer data-testid="site-footer" className="bg-[#050505] border-t border-gold/20 pt-20 pb-8">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-3 gap-12">
          <div>
            <img src={LOGO_URL} alt="Everett - Sam's Treescape" className="h-16 w-auto" />
            <p className="mt-6 text-gray-400 text-sm leading-relaxed max-w-xs">
              Family owned professional tree care you can trust. Serving Everett and surrounding areas
              (Lynnwood, Mukilteo, Edmonds, and more) with precision, safety, and pride.
            </p>
            <p className="mt-5 font-cursive text-gold text-2xl">Trees • Property • Pride</p>
            <div className="mt-6 flex items-center gap-4" data-testid="footer-qr">
              <img
                src="/qr-everett-gold.png"
                alt="Scan to visit everettsamstreescape.com"
                className="w-24 h-24 rounded-lg border border-gold/30"
              />
              <div>
                <p className="text-gold text-xs uppercase tracking-widest font-semibold">Scan Me</p>
                <p className="text-gray-400 text-xs mt-1 max-w-[10rem]">Point your camera here to visit &amp; save our site.</p>
              </div>
            </div>
            <div className="mt-6 flex items-center gap-3" data-testid="footer-socials">
              {SOCIALS.map((s) => {
                const Icon = SOCIAL_ICONS[s.icon];
                return (
                  <a
                    key={s.name}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.name}
                    data-testid={`social-${s.name.toLowerCase()}`}
                    className="w-10 h-10 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center text-gold hover:bg-gold hover:text-black transition-all"
                  >
                    <Icon size={18} />
                  </a>
                );
              })}
            </div>
          </div>

          <div>
            <h4 className="font-serif text-xl text-white mb-6">Contact</h4>
            <ul className="space-y-4 text-sm">
              <li>
                <a href={`tel:${CONTACT.ownerPhone}`} className="flex items-center gap-3 text-gray-300 hover:text-gold transition-colors">
                  <Phone size={16} className="text-gold" /> {CONTACT.ownerPhone} <span className="text-gold text-xs">(Owner)</span>
                </a>
              </li>
              <li>
                <a href={`tel:${CONTACT.dispatchPhone}`} className="flex items-center gap-3 text-gray-300 hover:text-gold transition-colors">
                  <Phone size={16} className="text-gold" /> {CONTACT.dispatchPhone} <span className="text-gold text-xs">(Dispatch)</span>
                </a>
              </li>
              <li>
                <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-3 text-gray-300 hover:text-gold transition-colors break-all">
                  <Mail size={16} className="text-gold" /> {CONTACT.email}
                </a>
              </li>
              <li className="flex items-center gap-3 text-gray-300">
                <Globe size={16} className="text-gold" /> {CONTACT.website}
              </li>
              <li className="flex items-center gap-3 text-gray-300">
                <MapPin size={16} className="text-gold" /> Serving Everett &amp; Surrounding Areas
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-serif text-xl text-white mb-6">Services</h4>
            <ul className="space-y-3 text-sm text-gray-400">
              {["Tree Removal", "Trimming & Pruning", "Stump Grinding & Removal", "Emergency & Storm Damage", "Land Clearing"].map((s) => (
                <li key={s}><a href="#services" className="hover:text-gold transition-colors">{s}</a></li>
              ))}
            </ul>
            <a href="#estimate" className="mt-6 inline-block bg-gold text-black font-bold px-6 py-3 rounded-sm hover:bg-gold-light transition-colors uppercase tracking-widest text-xs">
              Free Estimate
            </a>
          </div>
        </div>

        <div className="gold-divider mt-16" />
        <div className="mt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-gray-500">
          <p>© {new Date().getFullYear()} EVERETT - Sam's Treescape. All rights reserved.</p>
          <a href="/admin" data-testid="footer-admin-link" className="hover:text-gold transition-colors">Manage Leads</a>
        </div>
      </div>
    </footer>
  );
};
